import type { ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import InfiniteScrollPagingHandler from '../../../components/InfiniteScrollPagingHandler/InfiniteScrollPagingHandler';
import LoadError from '../../../components/LoadError';

export interface TimelinePagingHandlerProps {
  children: ReactNode;
  error?: Error | null;
  retry?: () => void | Promise<unknown>;
  hasPrevious: boolean;
  hasMore: boolean;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  isLoadingPrevious: boolean;
  itemCount: number;
  loadPrevious?: () => void | Promise<unknown>;
  loadMore?: () => void | Promise<unknown>;
}

const TimelineLoader = ({ initial = false }: { initial?: boolean }) => (
  <Box
    role="status"
    aria-label={initial ? 'Loading timeline' : 'Loading more timeline records'}
    sx={{ display: 'flex', justifyContent: 'center', p: initial ? 2 : 1 }}
  >
    <CircularProgress size={initial ? 24 : 20} />
  </Box>
);

const TimelinePagingHandler = ({
  children,
  error,
  retry,
  hasPrevious,
  hasMore,
  isInitialLoading,
  isLoadingMore,
  isLoadingPrevious,
  itemCount,
  loadPrevious,
  loadMore,
}: TimelinePagingHandlerProps) => (
  <InfiniteScrollPagingHandler
    hasMore={hasMore && !error}
    hasPrevious={hasPrevious && !error}
    isInitialLoading={isInitialLoading}
    isLoading={isLoadingMore}
    isLoadingPrevious={isLoadingPrevious}
    itemCount={itemCount}
    loadPrevious={loadPrevious}
    loadMore={loadMore}
    initialLoader={<TimelineLoader initial />}
    loader={<TimelineLoader />}
  >
    {error && (
      <LoadError
        message={
          itemCount
            ? 'Could not load timeline records. Previously loaded activity is still shown.'
            : 'Could not load activity.'
        }
        retry={retry}
        loading={isInitialLoading || isLoadingMore || isLoadingPrevious}
      />
    )}
    {(!error || itemCount > 0) && children}
  </InfiniteScrollPagingHandler>
);

export default TimelinePagingHandler;
